import { useParams, useNavigate } from "react-router-dom";
import RecipeForm, { type RecipeFormValues } from "../components/RecipeForm";
import { useRecipe } from "../hooks/useRecipe";
import { useUpdateRecipe } from "../hooks/useUpdateRecipe";
import { useAuth } from "../context/AuthContext";
import { uploadImage } from "../lib/uploadImage";

export default function EditRecipePage() {
  const { slug } = useParams<{ slug: string }>();
  const { data: recipe, isLoading, error } = useRecipe(slug!);
  const { mutateAsync } = useUpdateRecipe();
  const { getToken } = useAuth();
  const navigate = useNavigate();

  async function handleSubmit(values: RecipeFormValues, imageFile: File | null) {
    if (!recipe) return;
    const token = await getToken();
    if (!token) throw new Error("Not authenticated");

    let imageKey = values.imageKey || recipe.imageKey;
    let thumbnailKey = values.thumbnailKey || recipe.thumbnailKey;

    if (imageFile) {
      const uploaded = await uploadImage(imageFile, token);
      imageKey = uploaded.imageKey;
      thumbnailKey = uploaded.thumbnailKey;
    }

    const updated = await mutateAsync({
      recipeId: recipe.recipeId,
      data: { ...values, imageKey, thumbnailKey },
    });
    navigate(`/recipe/${updated.slug}`);
  }

  if (isLoading) {
    return (
      <div className="min-h-screen bg-cream flex items-center justify-center">
        <p className="text-gray-400">Loading…</p>
      </div>
    );
  }

  if (error || !recipe) {
    return (
      <div className="min-h-screen bg-cream flex items-center justify-center">
        <p className="text-red-500">Recipe not found.</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-cream">
      <div className="max-w-2xl mx-auto px-4 py-6">
        <h1 className="text-2xl font-semibold text-gray-900 mb-6" style={{ fontFamily: "var(--font-display)" }}>
          Edit Recipe
        </h1>
      </div>
      <RecipeForm
        initialValues={{
          title: recipe.title,
          slug: recipe.slug,
          description: recipe.description,
          course: recipe.course,
          cuisine: recipe.cuisine,
          prepTimeMinutes: recipe.prepTimeMinutes,
          cookTimeMinutes: recipe.cookTimeMinutes,
          servings: recipe.servings,
          tags: recipe.tags,
          ingredients: recipe.ingredients,
          steps: recipe.steps,
          notes: recipe.notes,
          source: recipe.source,
          sourceUrl: recipe.sourceUrl ?? "",
          rating: recipe.rating,
          isFavorite: recipe.isFavorite,
          imageKey: recipe.imageKey,
          thumbnailKey: recipe.thumbnailKey,
        }}
        onSubmit={handleSubmit}
        submitLabel="Save Changes"
      />
    </div>
  );
}
